import React, {
  useRef,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useState,
  useMemo,
} from 'react'
import { Button, Tooltip } from 'antd'
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons'
import './TemplateEditor.css'

interface IVariables {
  key: string
  label: string
}

export interface IVariableGroup {
  group: string
  items: IVariables[]
}

interface TemplateEditorProps {
  value?: string
  onChange?: (value: string) => void
  variables: IVariables[] | IVariableGroup[]
}

const TemplateEditor = forwardRef<any, TemplateEditorProps>(
  ({ value = '', onChange, variables = [] }, ref) => {
    const editorRef = useRef<HTMLDivElement>(null)
    const lastSelection = useRef<Range | null>(null)
    const isComposingRef = useRef(false)

    const lastAppliedValueRef = useRef<string | undefined>(undefined)

    const flatVariables: IVariables[] = useMemo(
      () =>
        variables.length > 0 && 'group' in variables[0]
          ? (variables as IVariableGroup[]).flatMap(g => g.items)
          : (variables as IVariables[]),
      [variables],
    )

    // Функция для преобразования HTML редактора в формат для хранения с сохранением тегов форматирования
    const convertToHtmlFormat = useCallback((html: string): string => {
      if (!html) return ''

      // Создаем временный элемент для парсинга HTML
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html

      // Функция для рекурсивной обработки узлов
      const processNode = (node: Node): string => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent || ''
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element
          const tagName = element.tagName.toLowerCase()

          if (tagName === 'span' && element.classList.contains('variable-marker')) {
            const variableKey = element.getAttribute('data-variable')
            return variableKey ? `{${variableKey}}` : ''
          }

          const allowedTags = ['b', 'i', 'u', 'strong', 'em', 'br']
          if (allowedTags.includes(tagName)) {
            if (tagName === 'br') {
              return '<br>'
            }

            let innerHTML = ''
            for (let i = 0; i < element.childNodes.length; i++) {
              innerHTML += processNode(element.childNodes[i])
            }

            const normalizedTag = tagName === 'strong' ? 'b' : tagName === 'em' ? 'i' : tagName
            return `<${normalizedTag}>${innerHTML}</${normalizedTag}>`
          }

          let result = ''
          for (let i = 0; i < element.childNodes.length; i++) {
            result += processNode(element.childNodes[i])
          }
          return result
        }
        return ''
      }

      let result = ''
      for (let i = 0; i < tempDiv.childNodes.length; i++) {
        result += processNode(tempDiv.childNodes[i])
      }
      return result
    }, [])

    const processInitialValue = useCallback(
      (inputValue: string): string => {
        if (!inputValue) return ''

        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = inputValue

        const processTextForVariables = (text: string): string => {
          let result = text
          flatVariables.forEach(variable => {
            const regex = new RegExp(`\\{${variable.key}\\}`, 'gi')
            result = result.replace(
              regex,
              `<span class="variable-marker" contenteditable="false" data-variable="${variable.key}">{${variable.label}}</span>`,
            )
          })
          return result
        }

        const processNode = (node: Node): string => {
          if (node.nodeType === Node.TEXT_NODE) {
            return processTextForVariables(node.textContent || '')
          }

          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element
            const tagName = element.tagName.toLowerCase()

            const allowedTags = ['b', 'i', 'u', 'strong', 'em', 'br']
            if (allowedTags.includes(tagName)) {
              if (tagName === 'br') {
                return '<br>'
              }

              let innerHTML = ''
              for (let i = 0; i < element.childNodes.length; i++) {
                innerHTML += processNode(element.childNodes[i])
              }
              return `<${tagName}>${innerHTML}</${tagName}>`
            }

            let result = ''
            for (let i = 0; i < element.childNodes.length; i++) {
              result += processNode(element.childNodes[i])
            }
            return result
          }
          return ''
        }

        let result = ''
        for (let i = 0; i < tempDiv.childNodes.length; i++) {
          result += processNode(tempDiv.childNodes[i])
        }
        return result
      },
      [flatVariables],
    )

    useEffect(() => {
      if (!editorRef.current) return
      if (lastAppliedValueRef.current === value) return

      const processedValue = processInitialValue(value || '')
      if (editorRef.current.innerHTML !== processedValue) {
        editorRef.current.innerHTML = processedValue
      }
      lastAppliedValueRef.current = value
    }, [value, processInitialValue])

    const saveSelection = useCallback(() => {
      if (!editorRef.current) return

      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) return

      const range = sel.getRangeAt(0)
      if (editorRef.current.contains(range.commonAncestorContainer)) {
        lastSelection.current = range.cloneRange()
      }
    }, [])

    const insertVariable = useCallback(
      (variableKey: string, variableLabel: string) => {
        if (!editorRef.current) return

        editorRef.current.focus()
        saveSelection()

        const sel = window.getSelection()
        if (!sel) return

        try {
          let range: Range

          if (sel.rangeCount > 0) {
            range = sel.getRangeAt(0)
            if (!editorRef.current.contains(range.commonAncestorContainer)) {
              range = document.createRange()
              range.selectNodeContents(editorRef.current)
              range.collapse(false)
            }
          } else {
            range = document.createRange()
            range.selectNodeContents(editorRef.current)
            range.collapse(false)
          }

          sel.removeAllRanges()
          sel.addRange(range)
          range.deleteContents()

          const variableSpan = document.createElement('span')
          variableSpan.className = 'variable-marker'
          variableSpan.setAttribute('contenteditable', 'false')
          variableSpan.dataset.variable = variableKey
          variableSpan.textContent = `{${variableLabel}}`

          range.insertNode(variableSpan)

          const newRange = document.createRange()
          newRange.setStartAfter(variableSpan)
          newRange.collapse(true)

          sel.removeAllRanges()
          sel.addRange(newRange)

          setTimeout(() => {
            if (editorRef.current && onChange) {
              const htmlValue = convertToHtmlFormat(editorRef.current.innerHTML)
              lastAppliedValueRef.current = htmlValue
              onChange(htmlValue)
            }
          }, 0)
        } catch (error) {
          console.warn('Error inserting variable:', error)
        }
      },
      [onChange, saveSelection, convertToHtmlFormat],
    )

    const handleInput = useCallback(() => {
      if (isComposingRef.current) return
      if (!editorRef.current || !onChange) return
      requestAnimationFrame(() => {
        const htmlValue = convertToHtmlFormat(editorRef.current?.innerHTML || '')
        lastAppliedValueRef.current = htmlValue
        onChange(htmlValue)
      })
    }, [onChange, convertToHtmlFormat])

    const handleCompositionStart = useCallback(() => {
      isComposingRef.current = true
    }, [])

    const handleCompositionEnd = useCallback(() => {
      isComposingRef.current = false
      handleInput()
    }, [handleInput])

    const handlePaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
      event.preventDefault()
      if (!editorRef.current) return
      const text = event.clipboardData.getData('text/plain')
      document.execCommand('insertText', false, text)
    }, [])

    const handleEditorClick = useCallback(() => {
      saveSelection()
    }, [saveSelection])

    const handleKeyUp = useCallback(() => {
      saveSelection()
    }, [saveSelection])

    const handleBlur = useCallback(() => {
      if (editorRef.current && onChange) {
        const htmlValue = convertToHtmlFormat(editorRef.current.innerHTML)
        lastAppliedValueRef.current = htmlValue
        onChange(htmlValue)
      }
    }, [onChange, convertToHtmlFormat])

    useImperativeHandle(ref, () => ({
      focus: () => editorRef.current?.focus(),
      blur: () => editorRef.current?.blur(),
    }))

    const tooltipPlacement = 'top'

    const isGrouped = variables.length > 0 && 'group' in variables[0]

    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(() => {
      if (!isGrouped) return {}
      return Object.fromEntries((variables as IVariableGroup[]).map(g => [g.group, false]))
    })

    const toggleGroup = useCallback((group: string) => {
      setCollapsedGroups(prev => ({ ...prev, [group]: !prev[group] }))
    }, [])

    const renderVariableButtons = (items: IVariables[]) =>
      items.map(variable => (
        <Button
          key={variable.key}
          size="small"
          type="primary"
          ghost
          onClick={() => insertVariable(variable.key, variable.label)}
        >
          {variable.label}
        </Button>
      ))

    return (
      <div style={{ minWidth: '450px' }}>
        <div>
          {isGrouped ? (
            (variables as IVariableGroup[]).map(group => {
              const collapsed = collapsedGroups[group.group]
              return (
                <div key={group.group}>
                  <div
                    onClick={() => toggleGroup(group.group)}
                    style={{
                      padding: '5px 8px',
                      backgroundColor: '#f5f5f5',
                      fontWeight: 600,
                      fontSize: 12,
                      color: '#262626',
                      borderBottom: '1px solid #e8e8e8',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'pointer',
                      userSelect: 'none',
                      marginTop: 4,
                    }}
                  >
                    {collapsed ? (
                      <CaretRightOutlined style={{ color: '#8c8c8c', fontSize: 10 }} />
                    ) : (
                      <CaretDownOutlined style={{ color: '#8c8c8c', fontSize: 10 }} />
                    )}
                    {group.group}
                  </div>
                  {!collapsed && (
                    <div
                      className="toolbarGroup"
                      style={{
                        paddingTop: 4,
                        gap: '4px',
                        display: 'flex',
                        flexWrap: 'wrap',
                      }}
                    >
                      {renderVariableButtons(group.items)}
                    </div>
                  )}
                </div>
              )
            })
          ) : (
            <div className="toolbarGroup">{renderVariableButtons(flatVariables)}</div>
          )}
        </div>
        <div
          style={{
            marginTop: 6,
            flexDirection: 'column',
            border: '1px solid #e0e0e0',
            borderRadius: '4px 4px 0 0',
          }}
        >
          <div
            className="toolbarGroup"
            style={{
              padding: 6,
            }}
          >
            <Tooltip title="Жирный" placement={tooltipPlacement}>
              <Button
                size="small"
                type="primary"
                ghost
                onClick={() => document.execCommand('bold', false)}
              >
                <b>B</b>
              </Button>
            </Tooltip>
            <Tooltip title="Курсив" placement={tooltipPlacement}>
              <Button
                size="small"
                type="primary"
                ghost
                onClick={() => document.execCommand('italic', false)}
              >
                <i>I</i>
              </Button>
            </Tooltip>
            <Tooltip title="Подчеркнутый" placement={tooltipPlacement}>
              <Button
                size="small"
                type="primary"
                ghost
                onClick={() => document.execCommand('underline', false)}
              >
                <u>U</u>
              </Button>
            </Tooltip>
          </div>
          <div
            ref={editorRef}
            className="template-editor"
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onClick={handleEditorClick}
            onKeyUp={handleKeyUp}
            onBlur={handleBlur}
            onPaste={handlePaste}
            style={{
              minHeight: '200px',
              borderTop: '1px solid #d9d9d9',
              padding: '8px',
              outline: 'none',
            }}
          />
        </div>
      </div>
    )
  },
)

TemplateEditor.displayName = 'TemplateEditor'

export default React.memo(TemplateEditor, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.variables) === JSON.stringify(nextProps.variables)
})
